export const MASS_BRANCHES = Object.freeze({
  'base-regression': 'BaseRegression',
  'overweight-mean': 'OverweightMean',
  'athletic-mean': 'AthleticMean',
});
export const MASS_AGE_BASIS = 'recorded-completed-years';
const versions = {
  ffm: 'GedipBioImp2-6109-Base-FFM-reference-1',
  fm: 'GedipBioImp2-6109-Base-FM-difference-reference-1',
  bcm: 'GedipBioImp2-6109-Base-BCM-factor-reference-1',
  bcmi: 'GedipBioImp2-6109-Base-BCMI-double-reference-1',
  bmr: 'GedipBioImp2-6109-Base-BMR-mean-reference-1',
};
const text = v => typeof v === 'string' && v.trim().length > 0;
const optionalText = v => v === null || typeof v === 'string';
const positive = v => typeof v === 'number' && Number.isFinite(v) && v > 0;
const readonly = v => v?.isClinicalValidated === false && v?.canSaveMeasurement === false;

export function validateMassChoices(branch, ageBasis) {
  if (!Object.prototype.hasOwnProperty.call(MASS_BRANCHES, branch) || ageBasis !== MASS_AGE_BASIS) {
    throw new Error('Seleziona la variante e conferma l’uso degli anni compiuti registrati.');
  }
}

// Contract validation only. No formula calculation, correction, storage or fallback.
export function validateMassReference(result, measurementId, branch, ageBasis) {
  validateMassChoices(branch, ageBasis);
  const invalid = () => { throw new Error('Risposta del confronto masse non valida. La misura salvata resta invariata.'); };
  const ref = result?.reference, mass = ref?.mass, context = result?.context;
  if (!text(measurementId) || result?.measurementId !== measurementId ||
      !readonly(result) || !readonly(ref) || !readonly(mass) || !text(result.savedFormulaVersion) ||
      !optionalText(result.patientContextVersion) || !text(result.warning) || !result.saved || !result.difference ||
      mass.selectedBranch !== MASS_BRANCHES[branch] || !context || context.branchSource !== 'explicit-request' ||
      !optionalText(context.reason) || !(context.recordedAgeYears === null ||
        (Number.isInteger(context.recordedAgeYears) && context.recordedAgeYears >= 0)) ||
      !(context.recordedBodyBuild === null || ['Normal', 'Overweight', 'Athletic'].includes(context.recordedBodyBuild))) invalid();
  const recovered = result.patientContextVersion === 'gedip-original-context-1';
  const known = recovered || result.patientContextVersion === 'patient-at-acquisition-1';
  if (context.ageBasis !== (recovered ? 'gedip-original-dob-completed-years' : known ? 'sibilla-recorded-completed-years' : 'not-selected')) invalid();
  if (context.status === 'recorded') {
    if (!known || !positive(context.recordedAgeYears) || context.reason !== null) invalid();
  } else if (context.status !== 'unavailable' || !text(context.reason)) invalid();
  if (!known && (context.recordedAgeYears !== null || context.recordedBodyBuild !== null)) invalid();

  const terms = [
    ['ffm', mass.ffm, 'kilograms', 'ffmKg', null, null],
    ['fm', mass.fm, 'kilograms', 'fmKg', null, null],
    ['bcm', ref.bcm, 'number', 'bcmKg', 'kg', versions.ffm],
    ['bcmi', ref.bcmi, 'number', 'bcmi', 'kg/m²', versions.bcm],
    ['bmr', ref.bmr, 'number', 'bmrKcalDay', 'kcal/day', versions.ffm],
  ];
  for (const [name, value, field, savedKey, unit, dependency] of terms) {
    const saved = result.saved[savedKey], difference = result.difference[savedKey];
    if (!value || value.isClinicalValidated !== false || value.formulaVersion !== versions[name] ||
        !optionalText(value.reason) || !(saved === null || positive(saved)) ||
        (unit && (value.unit !== unit || value.dependencyVersion !== dependency))) invalid();
    if (value.status === 'reference-only') {
      if (context.status !== 'recorded' || !positive(value[field]) || value.reason !== null ||
          (saved === null ? difference !== null : typeof difference !== 'number' || !Number.isFinite(difference))) invalid();
    } else if (value.status !== 'unavailable' || value[field] !== null || difference !== null || !text(value.reason)) invalid();
  }
  if (mass.ffm.status !== mass.fm.status ||
      (mass.ffm.status === 'unavailable' && [ref.bcm, ref.bcmi, ref.bmr].some(v => v.status !== 'unavailable')) ||
      (ref.bcmi.status === 'reference-only' && ref.bcm.status !== 'reference-only')) invalid();
  return result;
}
